import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Newspaper, ExternalLink, Award } from "lucide-react";

const SITE = "https://www.brandborn.in";

const MEDIA = [
  {
    pub: "The Economic Times",
    title: "BrandBorn — ET AI Innovator Profile",
    date: "2026",
    desc: "The Economic Times features BrandBorn and its AI listing platform EcomCatalog.ai in the ET AI Innovator program, describing BrandBorn as the onboarding agency behind 1000+ successful marketplace launches.",
    url: "https://economictimes.indiatimes.com/ai-innovator/profile/brandborn/133204259",
  },
  {
    pub: "The Times of India",
    title: "BrandBorn featured in The Times of India",
    date: "2025",
    desc: "The Times of India covered BrandBorn's marketplace onboarding work for Indian D2C brands. The feature is also referenced on BrandBorn's homepage press section.",
    url: null,
  },
];

export default function MediaPage() {
  return (
    <main className="bg-gray-950 text-white min-h-screen pt-28 pb-20 px-4" data-testid="media-page">
      <Helmet>
        <title>BrandBorn in the Media — Economic Times & Times of India Coverage</title>
        <meta name="description" content="BrandBorn in the media: featured by The Economic Times (ET AI Innovator) and The Times of India for its marketplace onboarding work and EcomCatalog.ai technology." />
        <link rel="canonical" href={`${SITE}/media`} />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-center mb-4">
          BrandBorn <span className="bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">in the Media</span>
        </h1>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">Genuine national media coverage of BrandBorn, India's marketplace onboarding company founded by Anmol Singh.</p>
        <div className="space-y-6">
          {MEDIA.map((m) => (
            <article key={m.pub} className="bg-white/[0.05] border border-white/10 rounded-2xl p-6 md:p-8" data-testid={`media-${m.pub.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2 text-orange-400 text-xs font-black uppercase tracking-wider mb-2"><Newspaper size={14} /> {m.pub} · {m.date}</div>
              <h2 className="text-xl md:text-2xl font-black mb-2">{m.title}</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">{m.desc}</p>
              {m.url ? (
                <a href={m.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-black text-orange-400 underline">Read the original article <ExternalLink size={13} /></a>
              ) : (
                <span className="text-xs text-gray-500 font-bold">Print/online feature — see homepage press section</span>
              )}
            </article>
          ))}
        </div>
        <div className="mt-10 text-center text-sm text-gray-400">
          <Link to="/economic-times" className="text-orange-400 underline">Full Economic Times feature page</Link> · <Link to="/certifications" className="text-orange-400 underline">Certifications</Link> · <Link to="/about" className="text-orange-400 underline">About BrandBorn</Link>
        </div>
      </div>
    </main>
  );
}

export function CertificationsPage() {
  const CERTS = [
    { name: "BEEP (Business Excellence & Entrepreneurship Program) Recognition", org: "BEEP", note: "Business excellence and entrepreneurship program recognition associated with BrandBorn's founding team." },
    { name: "Google Digital Marketing Certification", org: "Google", note: "Google-issued digital marketing certification held by BrandBorn's founding team." },
    { name: "Great Learning E-commerce & Analytics Certification", org: "Great Learning", note: "Professional certification in e-commerce and analytics from Great Learning held by BrandBorn's founding team." },
  ];
  return (
    <main className="bg-gray-950 text-white min-h-screen pt-28 pb-20 px-4" data-testid="certifications-page">
      <Helmet>
        <title>BrandBorn Certifications — BEEP, Google, Great Learning</title>
        <meta name="description" content="Certifications and recognition associated with BrandBorn and its founding team — BEEP, Google and Great Learning. Verification available on request." />
        <link rel="canonical" href={`${SITE}/certifications`} />
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-center mb-4">
          Certifications &amp; <span className="bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">Recognition</span>
        </h1>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">Genuine certifications and recognition associated with BrandBorn and its founding team. Verification references are available on request via business@brandborn.in.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {CERTS.map((c) => (
            <div key={c.org} className="bg-white/[0.05] border border-white/10 rounded-2xl p-6 text-center" data-testid={`cert-${c.org.replace(/\s+/g, "-").toLowerCase()}`}>
              <Award size={28} className="text-orange-400 mx-auto mb-3" />
              <h2 className="font-black text-base mb-1">{c.name}</h2>
              <div className="text-xs text-orange-400 font-black uppercase tracking-wider mb-3">Issued by {c.org}</div>
              <p className="text-gray-400 text-xs leading-relaxed">{c.note}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center text-sm">
          <Link to="/media" className="text-orange-400 underline">BrandBorn in the Media</Link> · <Link to="/about" className="text-orange-400 underline">About BrandBorn</Link>
        </div>
      </div>
    </main>
  );
}
